export const HOME_MARKET_ITEMS = [
  {
    itemType: "DIAMOND",
    label: "Diamond",
    iconSrc: "/imgs/minecraft-items/diamond.png",
  },
  {
    itemType: "GOLDEN_APPLE",
    label: "Golden Apple",
    iconSrc: "/imgs/minecraft-items/golden_apple.png",
  },
  {
    itemType: "QUARTZ_BLOCK",
    label: "Quartz Block",
    iconSrc: "/imgs/minecraft-items/quartz_block.png",
  },
];

export const HOME_PRICE_HISTORY_URL = "/home-price-history.json";

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function isValidMonth(month) {
  return typeof month === "string" && /^\d{4}-\d{2}$/.test(month);
}

export function parseNumericPrice(value) {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(String(value).trim());
  return Number.isFinite(n) ? n : null;
}

function addMonths(monthKey, delta) {
  const [year, month] = monthKey.split("-").map(Number);
  const date = new Date(year, month - 1 + delta, 1);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

export function getDatasetMaxMonth(rows) {
  let max = null;
  if (!Array.isArray(rows)) return null;
  for (const row of rows) {
    if (!row || !isValidMonth(row.month)) continue;
    if (!max || row.month > max) max = row.month;
  }
  return max;
}

export function getLastTwelveMonthKeys(maxMonth) {
  if (!isValidMonth(maxMonth)) return [];
  const keys = [];
  for (let i = 11; i >= 0; i -= 1) {
    keys.push(addMonths(maxMonth, -i));
  }
  return keys;
}

function normalizeItemType(value) {
  return String(value || "").trim().toUpperCase();
}

export function getLastTwelveMonthSeries(rows, itemType, maxMonth) {
  const end = maxMonth || getDatasetMaxMonth(rows);
  if (!end) return [];
  const wanted = normalizeItemType(itemType);
  const keys = getLastTwelveMonthKeys(end);
  const byMonth = Object.create(null);
  if (Array.isArray(rows)) {
    for (const row of rows) {
      if (!row || normalizeItemType(row.item_type) !== wanted || !isValidMonth(row.month)) {
        continue;
      }
      const raw =
        row.avg_price_per_unit != null ? row.avg_price_per_unit : row.price;
      byMonth[row.month] = parseNumericPrice(raw);
    }
  }
  return keys.map((month) => ({
    month,
    price: Object.prototype.hasOwnProperty.call(byMonth, month)
      ? byMonth[month]
      : null,
  }));
}

export function getPeakAndFloor(series) {
  let peak = null;
  let floor = null;
  if (!Array.isArray(series)) return { peak, floor };
  series.forEach((point, index) => {
    const price = parseNumericPrice(point && point.price);
    if (price == null) return;
    if (!peak || price > peak.price) {
      peak = { month: point.month, price, index };
    }
    if (!floor || price < floor.price) {
      floor = { month: point.month, price, index };
    }
  });
  return { peak, floor };
}

export function formatMarketPrice(value) {
  const n = parseNumericPrice(value);
  if (n == null) return "";
  const abs = Math.abs(n);
  if (abs >= 100) {
    return `$${Math.round(n)}`;
  }
  const rounded = Math.round(n * 100) / 100;
  if (Number.isInteger(rounded)) {
    return `$${rounded}`;
  }
  return `$${rounded.toFixed(2).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "")}`;
}

export function formatMonthTick(month) {
  if (!isValidMonth(month)) return "";
  const [year, m] = month.split("-").map(Number);
  return `${MONTH_SHORT[m - 1]} ${String(year).slice(2)}`;
}

export function parseMonthlyTsv(text) {
  if (!text || typeof text !== "string") return [];
  const lines = text.split(/\r?\n/).filter((line) => line.length > 0);
  if (lines.length === 0) return [];
  const header = lines[0].split("\t");
  const monthIdx = header.indexOf("month");
  const typeIdx = header.indexOf("item_type");
  const priceIdx = header.indexOf("avg_price_per_unit");
  if (monthIdx < 0 || typeIdx < 0 || priceIdx < 0) return [];
  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cols = lines[i].split("\t");
    const month = String(cols[monthIdx] || "").trim();
    if (month === "month" || !isValidMonth(month)) continue;
    rows.push({
      month,
      item_type: String(cols[typeIdx] || "").trim(),
      avg_price_per_unit: cols[priceIdx],
    });
  }
  return rows;
}

export function buildHomePriceHistory(rows, generatedAt = new Date().toISOString()) {
  const endMonth = getDatasetMaxMonth(rows);
  const items = {};
  HOME_MARKET_ITEMS.forEach((item) => {
    items[item.itemType] = {
      label: item.label,
      itemType: item.itemType,
      series: endMonth
        ? getLastTwelveMonthSeries(rows, item.itemType, endMonth)
        : [],
    };
  });
  return { generatedAt, endMonth, items };
}

export function getLatestPrice(series) {
  if (!Array.isArray(series)) return null;
  for (let i = series.length - 1; i >= 0; i -= 1) {
    const price = parseNumericPrice(series[i] && series[i].price);
    if (price != null) return price;
  }
  return null;
}

export function getMarketHistoryEntry(history, itemType) {
  const items = history && history.items;
  if (!items || typeof items !== "object") return null;
  if (items[itemType]) return items[itemType];
  const wanted = normalizeItemType(itemType);
  const key = Object.keys(items).find((name) => normalizeItemType(name) === wanted);
  return key ? items[key] : null;
}

export function marketsItemHref(itemType) {
  return `/markets?tab=historical-monthly&monthly_item=${encodeURIComponent(
    itemType
  )}&monthly_time=1_year`;
}
